/**
 * A raiz de composicao do app: cliente HTTP, TanStack Query, sessao e regioes vivas.
 *
 * Cada linha aqui e uma decisao de um documento, e o comentario diz qual — porque este arquivo e
 * o unico lugar do app onde essas decisoes se encontram, e trocar um default aqui muda o
 * comportamento de todas as telas ao mesmo tempo.
 */

import { ApiClientProvider } from '@farmacia/api-client/query';
import { createApiClient, type ApiClient } from '@farmacia/api-client';
import { LiveRegions } from '@farmacia/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';

import { ProvedorDeMinhaLoja } from '../loja/contexto.jsx';
import { SelecaoAutomaticaDeLoja } from '../loja/SeletorDeLoja.jsx';

import { lerAmbiente, type Ambiente } from './ambiente.js';
import { ProvedorDeSessao } from './sessao.jsx';

export interface CriarClienteDeQueryOpcoes {
  /** `PWA-020` — o timeout de query, que vem do estado de conexao. */
  readonly retentativas?: number;
}

/**
 * O `QueryClient`.
 *
 * **Nao ha `staleTime` nem `gcTime` global.** Eles vem por familia, das fabricas de
 * `@farmacia/api-client/query`, que os derivam do `Cache-Control` que a propria API declara
 * (`cache-policy.ts`). Um default global aqui seria uma segunda politica de cache, e o
 * `PWA §4.2` e explicito ao querer uma so (X-05) — pior: o default venceria em toda query que
 * alguem escrevesse sem a fabrica.
 */
export function criarClienteDeQuery(opcoes: CriarClienteDeQueryOpcoes = {}): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Numero de retentativas so; o *quando* e o *quanto esperar* sao do cliente HTTP, que
        // le `Retry-After` e o header `RateLimit` (`API §9`). Duas politicas de retentativa
        // seriam uma cega ao header.
        retry: opcoes.retentativas ?? 1,
        // Franquia: o publico conta MB (`PWA §0.1`). Refazer a busca porque a pessoa voltou
        // para a aba e gastar o dado dela para adivinhar que algo mudou.
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
      },
      mutations: {
        // Repetir uma mutacao sem chave de idempotencia cria pedido duplicado. Onde a chave
        // existe (`POST /v1/orders`), quem repete e o cliente HTTP, que a reusa (`API §3`).
        retry: false,
      },
    },
  });
}

export interface ProvedoresProps {
  readonly children: ReactNode;
  /** Injetavel para o teste montar a arvore com um cliente duble. */
  readonly cliente?: ApiClient;
  readonly queryClient?: QueryClient;
  readonly ambiente?: Ambiente;
  readonly aoPerderSessao?: () => void;
}

export function Provedores({
  children,
  cliente,
  queryClient,
  ambiente,
  aoPerderSessao,
}: ProvedoresProps): ReactNode {
  const [queryClientLocal] = useState(() => queryClient ?? criarClienteDeQuery());

  const clienteLocal = useMemo<ApiClient>(() => {
    if (cliente !== undefined) return cliente;
    const env = ambiente ?? lerAmbiente(import.meta.env);
    return createApiClient({
      baseUrl: env.origemDaApi,
      // `API §1.2` — `X-Client` e obrigatorio e dirige feature flag e telemetria.
      client: { id: 'web-cliente', version: env.buildId },
      // `PWA-034` — o gancho de `clearUserScopedStorage` em 401/403. O cliente HTTP nao limpa
      // nada sozinho (nao conhece IndexedDB nem Cache Storage): ele avisa.
      ...(aoPerderSessao === undefined ? {} : { onSessionLost: aoPerderSessao }),
    });
  }, [cliente, ambiente, aoPerderSessao]);

  return (
    <QueryClientProvider client={queryClientLocal}>
      <ApiClientProvider client={clienteLocal}>
        <ProvedorDeSessao>
          {/*
            `PWA §10.2` — a filial e selecao MANUAL, lembrada no aparelho. O provedor fica
            DENTRO do `ApiClientProvider` porque `<SelecaoAutomaticaDeLoja />` consulta
            `GET /v1/stores`; e fica FORA do `<LiveRegions>` porque o cabecalho, que mostra a
            filial, e irmao do conteudo, nao filho dele.
          */}
          <ProvedorDeMinhaLoja>
            {/*
              Sem marcacao: so resolve o caso em que nao ha escolha a fazer (instalacao com uma
              unica filial). Com filial ja escolhida nao dispara requisicao nenhuma.
            */}
            <SelecaoAutomaticaDeLoja />
            {/*
              DESIGN_SYSTEM §3.5 — as regioes vivas moram na RAIZ, uma vez. Componente nao cria
              regiao viva propria: duas regioes `aria-live` competindo fazem o leitor de tela
              anunciar a mesma coisa duas vezes, ou nenhuma.
            */}
            <LiveRegions>{children}</LiveRegions>
          </ProvedorDeMinhaLoja>
        </ProvedorDeSessao>
      </ApiClientProvider>
    </QueryClientProvider>
  );
}
